import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Button,
  Card,
  Badge,
  Caption1,
  Text,
  SearchBox,
  Dropdown,
  Option,
  Field,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowDownloadRegular,
  DocumentBulletListRegular,
  EyeRegular,
} from '@fluentui/react-icons'
import { Template } from '@templatean/shared/types'
import { templateService } from '@templatean/shared/services'
import { formatDate } from '@templatean/shared/utils'
import { useNotify } from '../hooks/useNotify'
import { ListSkeleton, EmptyState, ErrorState, SectionHeader } from './ui'

interface TemplateListProps {
  onSelect: (template: Template) => void
}

/** Cards shown before "Show more"; keeps a long catalogue scannable in a narrow pane. */
const PAGE_SIZE = 8

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  filters: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  search: { width: '100%' },
  dropdown: { width: '100%', minWidth: 0 },
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  card: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    rowGap: tokens.spacingVerticalXXS,
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    ...shorthands.overflow('hidden'),
    color: tokens.colorNeutralForeground2,
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
  actions: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  // Primary action fills the row, as Office panes present the main command.
  grow: { flexGrow: 1 },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXS,
  },
  // The preview renders the template's own markup, so it needs readable defaults
  // rather than inheriting the pane's tight layout.
  preview: {
    maxHeight: '55vh',
    overflowY: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    '& h1': { fontSize: tokens.fontSizeBase500, marginTop: 0 },
    '& h2': { fontSize: tokens.fontSizeBase400 },
    '& table': { width: '100%', borderCollapse: 'collapse' },
    '& td, & th': {
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
      ...shorthands.padding('4px', '6px'),
      textAlign: 'left',
    },
  },
  previewPlain: { whiteSpace: 'pre-wrap' },
})

const TemplateList: React.FC<TemplateListProps> = ({ onSelect }) => {
  const styles = useStyles()
  const notify = useNotify()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [insertingId, setInsertingId] = useState<string | null>(null)
  const [previewingId, setPreviewingId] = useState<string | null>(null)
  /** The template shown in the preview dialog, once its full body has loaded. */
  const [preview, setPreview] = useState<Template | null>(null)

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await templateService.getTemplates({
        type: 'Word',
        isPublished: true,
        pageSize: 100,
      })
      setTemplates(response.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The template library did not respond.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadTemplates()
  }, [loadTemplates])

  const categories = useMemo(
    () => Array.from(new Set(templates.map(t => t.category).filter(Boolean))).sort(),
    [templates]
  )

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return templates.filter(
      template =>
        (selectedCategory === 'all' || template.category === selectedCategory) &&
        (query === '' ||
          template.name.toLowerCase().includes(query) ||
          (template.description?.toLowerCase().includes(query) ?? false))
    )
  }, [templates, searchQuery, selectedCategory])

  // A new search starts from the top of the results.
  useEffect(() => setVisible(PAGE_SIZE), [searchQuery, selectedCategory])

  const shown = filtered.slice(0, visible)
  const busy = insertingId !== null || previewingId !== null

  const handleInsert = async (template: Template) => {
    if (busy) return

    const body = template.content.body?.trim()
    if (!body) {
      notify({
        intent: 'warning',
        title: 'Nothing to insert',
        body: `"${template.name}" has no content yet.`,
      })
      return
    }

    setInsertingId(template.id)
    try {
      await Word.run(async context => {
        // Insert at the cursor, replacing whatever is selected, which is what Office
        // commands do. Appending to the end put the template somewhere the user was
        // not looking.
        const selection = context.document.getSelection()
        if (template.content.format === 'html') {
          selection.insertHtml(body, Word.InsertLocation.replace)
        } else {
          selection.insertText(body, Word.InsertLocation.replace)
        }
        await context.sync()
      })

      notify({ title: 'Template inserted', body: template.name })
      onSelect(template)
    } catch (err) {
      notify({
        intent: 'error',
        title: 'Could not insert the template',
        body: err instanceof Error ? err.message : 'Word refused the request.',
      })
    } finally {
      setInsertingId(null)
    }
  }

  const handlePreview = async (template: Template) => {
    if (busy) return

    setPreviewingId(template.id)
    try {
      // The list may carry a trimmed body, so load the full template to show it.
      const full = await templateService.getTemplate(template.id)
      setPreview(full)
      onSelect(full)
    } catch (err) {
      notify({
        intent: 'error',
        title: 'Could not open the preview',
        body: err instanceof Error ? err.message : 'The template did not load.',
      })
    } finally {
      setPreviewingId(null)
    }
  }

  if (loading) return <ListSkeleton rows={4} label="Loading templates" />

  if (error) {
    return <ErrorState title="Templates unavailable" detail={error} onRetry={loadTemplates} />
  }

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <SearchBox
          className={styles.search}
          placeholder="Search templates"
          aria-label="Search templates"
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
        />
        <Field label="Category">
          <Dropdown
            className={styles.dropdown}
            selectedOptions={[selectedCategory]}
            value={selectedCategory === 'all' ? 'All categories' : selectedCategory}
            onOptionSelect={(_, data) => setSelectedCategory(data.optionValue ?? 'all')}
          >
            <Option value="all">All categories</Option>
            {categories.map(category => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      <div>
        <SectionHeader action={<Caption1>{filtered.length}</Caption1>}>Templates</SectionHeader>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<DocumentBulletListRegular fontSize={32} primaryFill={tokens.colorNeutralForeground3} />}
            title="No templates match"
            hint="Try a different search term, or choose another category."
            action={
              <Button
                appearance="secondary"
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategory('all')
                }}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <div className={styles.list}>
            {shown.map(template => {
              const inserting = insertingId === template.id
              const previewing = previewingId === template.id

              return (
                <Card key={template.id} className={styles.card}>
                  <Text weight="semibold">{template.name}</Text>
                  {template.description && (
                    <Caption1 className={styles.description}>{template.description}</Caption1>
                  )}

                  <div className={styles.meta}>
                    <Caption1>{template.category}</Caption1>
                    <Caption1>{formatDate(template.updatedAt)}</Caption1>
                  </div>

                  {template.tags.length > 0 && (
                    <div className={styles.tags}>
                      {template.tags.slice(0, 4).map(tag => (
                        <Badge key={tag} appearance="tint" color="brand" size="small">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className={styles.actions}>
                    <Button
                      className={styles.grow}
                      appearance="primary"
                      size="small"
                      icon={inserting ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
                      disabled={busy}
                      onClick={() => handleInsert(template)}
                    >
                      {inserting ? 'Inserting' : 'Insert'}
                    </Button>
                    <Button
                      appearance="secondary"
                      size="small"
                      icon={previewing ? <Spinner size="tiny" /> : <EyeRegular />}
                      disabled={busy}
                      onClick={() => handlePreview(template)}
                    >
                      Preview
                    </Button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {preview && (
        <Dialog open onOpenChange={(_, data) => !data.open && setPreview(null)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{preview.name}</DialogTitle>
              <DialogContent>
                {preview.description && <Caption1>{preview.description}</Caption1>}
                {preview.content.format === 'html' ? (
                  <div
                    className={styles.preview}
                    // The body is template content authored in Templatean, shown as it
                    // will be inserted. Rendering it is the whole point of a preview.
                    dangerouslySetInnerHTML={{ __html: preview.content.body }}
                  />
                ) : (
                  <div className={mergeClasses(styles.preview, styles.previewPlain)}>
                    {preview.content.body}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setPreview(null)}>
                  Close
                </Button>
                <Button
                  appearance="primary"
                  icon={<ArrowDownloadRegular />}
                  onClick={() => {
                    const target = preview
                    setPreview(null)
                    void handleInsert(target)
                  }}
                >
                  Insert
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {visible < filtered.length && (
        <div className={styles.footer}>
          <Button appearance="subtle" onClick={() => setVisible(v => v + PAGE_SIZE)}>
            Show more
          </Button>
          <Caption1>
            Showing {shown.length} of {filtered.length}
          </Caption1>
        </div>
      )}
    </div>
  )
}

export default TemplateList
